import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

export const JIRA_ISSUE_TYPES = ["Task", "Bug", "Story"] as const;
export type JiraIssueType = (typeof JIRA_ISSUE_TYPES)[number];

/**
 * Create Jira Issue Step
 *
 * Creates an issue in the Jira project configured on the org's Jira
 * integration. Resolves the recipient by their internal user id (looking up
 * the email) or by email directly, then by Jira accountId. The issue is
 * assigned to that user. Fails the step if the email can't be matched to a
 * Jira user.
 *
 * Title and body come from inputs by default. The `titleOverride` and
 * `bodyOverride` config fields exist for testing convenience — when set,
 * they replace the input values without changing the wiring.
 */
export const createJiraIssueStep = defineStepConfig({
  type: "create_jira_issue",
  name: "Create Jira Issue",
  description:
    "Creates a Jira issue assigned to a user (resolved by Gently email). Uses the org's Jira integration credentials and default project.",
  category: "Integration",

  inputSchema: z
    .object({
      userId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "Internal ERP user id. The user's email is used to resolve a Jira account. Either userId or userEmail must be provided.",
        ),
      userEmail: z
        .string()
        .optional()
        .describe(
          "Email used to resolve a Jira account directly. Either userId or userEmail must be provided.",
        ),
      title: z.string().describe("Issue summary / title"),
      body: z.string().describe("Issue description body (plain text)"),
    })
    .refine((input) => !!input.userId || !!input.userEmail, {
      message: "Either userId or userEmail must be provided.",
      path: ["userId"],
    }),

  outputSchema: z.object({
    issueKey: z.string().describe("Jira issue key, e.g. ENG-1234"),
    issueUrl: z.string().describe("Browse URL for the created issue"),
  }),

  configSchema: z.object({
    issueType: z.enum(JIRA_ISSUE_TYPES).prefault("Task"),
    priority: z
      .string()
      .optional()
      .describe("Jira priority name (e.g. 'High'). Optional."),
    labels: z
      .array(z.string())
      .optional()
      .describe("Labels applied to the created issue"),
    titleOverride: z
      .string()
      .optional()
      .describe(
        "Testing convenience: when set, replaces the input title without rewiring upstream sockets.",
      ),
    bodyOverride: z
      .string()
      .optional()
      .describe(
        "Testing convenience: when set, replaces the input body without rewiring upstream sockets.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
